<template>
  <div class="space-y-6">
    <!-- 标题栏 -->
    <div class="flex items-center justify-between">
      <h2 class="text-2xl font-bold text-white flex items-center gap-2">
        <i class="ph-bold ph-globe text-blue-400"></i>
        HTTP/TCP监控
      </h2>
      <button
        @click="$emit('refresh')"
        class="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white transition-colors flex items-center gap-2"
      >
        <i class="ph-bold ph-arrow-clockwise"></i>
        刷新
      </button>
    </div>

    <!-- 统计卡片 -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- 在线用户数 -->
      <div class="glass-card p-6 rounded-xl border border-slate-700 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div class="relative z-10">
          <div class="mb-3">
            <h3 class="text-sm text-slate-400 mb-2">在线用户</h3>
          </div>
          <div class="text-2xl font-bold text-white">{{ onlineUsers.length }}</div>
        </div>
      </div>

      <!-- 总访问量 -->
      <div class="glass-card p-6 rounded-xl border border-slate-700 relative overflow-hidden group">
        <div class="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div class="relative z-10">
          <div class="mb-3">
            <h3 class="text-sm text-slate-400 mb-2">总访问量</h3>
          </div>
          <div class="text-2xl font-bold text-white">{{ accessStats.totalAccess || 0 }}</div>
        </div>
      </div>
    </div>

    <!-- 在线用户列表 -->
    <div class="glass-card p-6 rounded-xl border border-slate-700">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-sm text-slate-400 mb-2">在线用户列表</h3>
        <span class="text-xs text-slate-400 bg-slate-800 px-3 py-1 rounded-full">
          共 {{ onlineUsers.length }} 人
        </span>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-16 text-slate-400">
        <div class="relative w-16 h-16 mb-6">
          <!-- 旋转的圆环 -->
          <div class="absolute inset-0 rounded-full border-4 border-transparent loader-ring" style="border-top-color: #06b6d4; border-right-color: #06b6d4; border-bottom-color: #475569; border-left-color: #475569;"></div>
          <!-- 内层静态圆 -->
          <div class="absolute inset-4 bg-slate-800 rounded-full"></div>
        </div>
        <div class="flex items-center gap-2">
          <span class="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style="animation-delay: 0s"></span>
          <span class="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></span>
          <span class="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style="animation-delay: 0.4s"></span>
        </div>
        <p class="text-sm mt-4 animate-pulse">正在加载在线用户数据...</p>
      </div>

      <!-- 用户列表 -->
      <div v-else-if="onlineUsers && onlineUsers.length > 0" class="space-y-3">
        <div
          v-for="user in onlineUsers"
          :key="user.id"
          class="bg-slate-800/50 hover:bg-slate-800 rounded-lg p-4 border border-slate-700/50 hover:border-cyan-500/30 transition-all group"
        >
          <div class="flex items-center justify-between">
            <!-- 左侧：用户信息 -->
            <div class="flex items-center gap-4 flex-1">
              <!-- 用户详情 -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-white font-semibold">{{ user.username || '未知用户' }}</span>
                  <span v-if="user.name" class="text-slate-400 text-sm">({{ user.name }})</span>
                  <span
                    v-if="user.role"
                    class="text-xs px-2 py-0.5 rounded-full font-medium"
                    :class="{
                      'bg-blue-500/20 text-blue-400 border border-blue-500/30': user.role === 'owner',
                      'bg-purple-500/20 text-purple-400 border border-purple-500/30': user.role === 'op',
                      'bg-green-500/20 text-green-400 border border-green-500/30': user.role === 'admin',
                      'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30': user.role === 'observer',
                      'bg-slate-500/20 text-slate-400 border border-slate-500/30': user.role === 'restricted' || user.role === 'user'
                    }"
                  >
                    {{ getRoleLabel(user.role) }}
                  </span>
                </div>
                
                <!-- IP地址和最后登录时间 -->
                <div class="flex items-center gap-4 text-xs text-slate-400">
                  <div class="flex items-center gap-1.5">
                    <i class="ph-bold ph-globe text-cyan-400"></i>
                    <span class="font-mono">{{ user.ipAddress || 'unknown' }}</span>
                  </div>
                  <div v-if="user.lastLoginTime" class="flex items-center gap-1.5">
                    <i class="ph-bold ph-clock text-slate-500"></i>
                    <span>{{ formatTime(user.lastLoginTime) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 右侧：在线状态 -->
            <div class="flex items-center gap-3 flex-shrink-0">
              <div class="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/30">
                <div class="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span class="text-green-400 text-xs font-medium">在线</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="flex flex-col items-center justify-center py-12 text-slate-400">
        <div class="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mb-4">
          <i class="ph-bold ph-user-circle text-3xl text-slate-600"></i>
        </div>
        <p class="text-sm font-medium mb-1">暂无在线用户</p>
        <p class="text-xs text-slate-500">当前没有活跃的用户会话</p>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  onlineUsers: {
    type: Array,
    default: () => []
  },
  accessStats: {
    type: Object,
    default: () => ({})
  },
  loading: {
    type: Boolean,
    default: false
  }
});

defineEmits(['refresh']);

// 格式化时间
const formatTime = (time) => {
  if (!time) return '未知';
  
  const date = new Date(time);
  const now = new Date();
  const diff = now - date;
  
  // 小于1分钟
  if (diff < 60000) {
    return '刚刚';
  }
  // 小于1小时
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `${minutes}分钟前`;
  }
  // 小于24小时
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours}小时前`;
  }
  // 超过24小时，显示具体日期
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month}-${day} ${hours}:${minutes}`;
};

// 获取角色标签
const getRoleLabel = (role) => {
  const roleMap = {
    'op': '运维管理',
    'owner': '主账号',
    'admin': '子账号（管理）',
    'observer': '子账号（只读）',
    'restricted': '子账号（受限）',
    'user': '普通用户'
  };
  return roleMap[role] || role;
};
</script>

<style scoped>
.glass-card {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loader-ring {
  animation: spin 1s linear infinite;
}
</style>
